import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient, ApiError } from '../lib/api';

const DEFAULT_BODY = `# {{skill name}}

Describe how this skill should be used. Frontmatter is optional in this MVP — the editor will round-trip whatever you write.
`;

export function CreateSkill() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const handleDraftWithAI = async () => {
    setError(null);
    if (!aiPrompt.trim()) {
      setError('Describe the skill first.');
      return;
    }
    setAiBusy(true);
    try {
      const result = await apiClient.broker.call({
        prompt: aiPrompt,
        role: 'create-skill',
      });
      setBody(result.text);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'AI draft failed');
    } finally {
      setAiBusy(false);
    }
  };

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const created = await apiClient.skills.create({
        name: name.trim(),
        description: description.trim() || undefined,
        body,
      });
      navigate(`/skill/${encodeURIComponent(created.id)}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="grid h-full grid-cols-1 bg-canvas md:grid-cols-[1fr_360px]">
      <section className="flex min-h-[60vh] flex-col overflow-hidden md:min-h-0">
        <div className="hairline-b flex items-center justify-between bg-surface px-4 py-2">
          <Link to="/library" className="text-[12px] text-text-muted hover:text-[var(--color-fg)]">
            ← Library
          </Link>
          <button
            type="button"
            onClick={handleCreate}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Creating…' : 'Create skill'}
          </button>
        </div>
        <div className="hairline-b px-4 py-3">
          <label className="block text-[10px] uppercase tracking-wider text-text-subtle">
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full bg-transparent font-mono text-[16px] outline-none"
              placeholder="my-new-skill"
              autoFocus
            />
          </label>
          <label className="mt-2 block text-[10px] uppercase tracking-wider text-text-subtle">
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 block w-full resize-none bg-transparent text-[13px] outline-none"
              placeholder="One sentence — what does this skill do?"
            />
          </label>
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          spellCheck={false}
          className="w-full flex-1 resize-none bg-transparent p-4 font-mono text-[13px] leading-[20px] outline-none"
        />
        {error && (
          <p className="hairline-t px-4 py-2 text-[12px] text-[var(--color-danger)]">{error}</p>
        )}
      </section>

      <aside className="hairline-t md:hairline-t-0 md:hairline-l bg-surface p-4 text-[12px]">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          AI Create flow
        </p>
        <p className="mt-1 text-[12px] text-text-subtle">
          Describe the skill in a sentence or two. AI drafts the body. You can still edit it
          before saving.
        </p>
        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          rows={6}
          className="mt-3 block w-full rounded border bg-canvas p-2 text-[13px] outline-none focus:border-[var(--color-accent)]"
          placeholder="e.g., a skill for triaging github issues and tagging them by severity"
        />
        <button
          type="button"
          onClick={handleDraftWithAI}
          disabled={aiBusy}
          className="btn btn-primary mt-2 w-full justify-center"
        >
          {aiBusy ? 'Drafting…' : 'Draft with AI'}
        </button>
        <p className="mt-3 text-[11px] text-text-subtle">
          Routes through <code className="font-mono">/broker/call</code> — the single AI
          ingress per the lifecycle contract.
        </p>
      </aside>
    </div>
  );
}
